import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { cn } from "@tellescope/ui";
import { getEventCellColorsFromHex } from "../../utils";
import { CSSProperties, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDraggable } from "@dnd-kit/core";
import { useDateFnsFormattersWithLocale } from "@tellescope/ui";

export interface MonthViewEventCellProps {
  event: TellescopeModelFromName<"calendar_events">;
  fromPrevious: boolean;
  continuesToNext: boolean;
  className?: string;
  onClick?: (() => void) | { to: string; replace?: boolean };
  showTitleOverride?: boolean;
  timezone?: string;
  draggable?: boolean;
  sourceDayStart?: number;
}

export const MonthViewEventCell = ({
  event,
  fromPrevious,
  continuesToNext,
  className,
  onClick,
  showTitleOverride,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  draggable = false,
  sourceDayStart,
}: MonthViewEventCellProps) => {
  const { formatInTimeZone } = useDateFnsFormattersWithLocale();
  const eventStartDate = new Date(event.startTimeInMS);

  const { resolvedColor, opacity, strikethrough } = useMemo(() => {
    if (event.cancelledAt || event.rescheduledAt) {
      return { resolvedColor: "#000000", opacity: 0.5, strikethrough: true };
    }

    return { resolvedColor: event.color };
  }, [event.cancelledAt, event.color, event.rescheduledAt]);

  const { backgroundColor, borderColor, textColor } = getEventCellColorsFromHex(resolvedColor);

  const moveDraggable = useDraggable({
    id: `month-event-move-${event.id}-${sourceDayStart ?? event.startTimeInMS}`,
    data: { type: "move-day", event, sourceDayStart: sourceDayStart ?? event.startTimeInMS },
    disabled: !draggable,
  });

  const Component = onClick ? (typeof onClick === "function" ? "button" : Link) : "div";
  const showTitle = !fromPrevious || showTitleOverride;

  const wrapperStyle: CSSProperties = {
    touchAction: draggable ? "none" : undefined,
    cursor: draggable ? (moveDraggable.isDragging ? "grabbing" : "grab") : undefined,
    opacity: moveDraggable.isDragging ? 0 : opacity,
  };

  return (
    <div
      ref={draggable ? moveDraggable.setNodeRef : undefined}
      data-testid={`calendar-event-${event.id}-${sourceDayStart ?? event.startTimeInMS}`}
      className={cn(
        "w-full px-1 h-[26px] relative",
        fromPrevious && "pl-0",
        continuesToNext && "pr-0",
        strikethrough && "line-through"
      )}
      style={wrapperStyle}
      {...(draggable ? moveDraggable.listeners : {})}
      {...(draggable ? moveDraggable.attributes : {})}
    >
      <Component
        style={{ background: backgroundColor, borderColor, color: textColor }}
        className={cn(
          "border rounded-sm z-10 py-1 px-2 w-full text-nowrap text-xs flex justify-between gap-1 h-full",
          fromPrevious && "border-l-0 rounded-l-none ml-[-1px] w-[calc(100%+1px)]",
          continuesToNext && "border-r-0 rounded-r-none mr-[-1px]",
          className
        )}
        to={typeof onClick === "object" && typeof Component !== "string" ? onClick.to : ""}
        replace={typeof onClick === "object" ? onClick.replace : undefined}
        onClick={(e) => {
          if (onClick !== undefined) e.stopPropagation();
          if (typeof onClick === "function") onClick();
        }}
      >
        {showTitle && (
          <>
            <span className="truncate flex-grow font-semibold text-left">{event.displayTitle || event.title}</span>
            <span>{formatInTimeZone(eventStartDate, timezone, "hh:mm a")}</span>
          </>
        )}
      </Component>
    </div>
  );
};
